import { memo, useCallback, useMemo } from 'react';

import Paginate from '@atlaskit/pagination';
import Spinner from '@atlaskit/spinner';

import { PIPELINE_PAGE_SIZE } from 'src/components/pipelines/constants';
import {
  Capabilities,
  Pipeline,
  Repository,
} from 'src/components/pipelines/models';
import { publishUiEvent } from 'src/utils/analytics/publish';

import PipelineRow from './PipelineRow';
import { EmptyQuery, ListWrapper, Loading, Pagination } from './styled';
import { uiEvents } from './uiEvents';

export const canUserStopPipelines = (
  repository: Repository,
  capabilities: Capabilities
): boolean => {
  if (!repository || !capabilities) {
    return false;
  }
  return !!(
    repository.canWrite &&
    capabilities.hasFetchedCapabilities &&
    capabilities.pipelinesEnabled
  );
};

export type Props = {
  isLoading: boolean;
  onPageChange: (...props: any) => void;
  page: number;
  currentPipelines: Pipeline[];
  pipelinesSize: number;
  repository: Repository;
  capabilities: Capabilities;
};

const EmptyState = () => (
  <tr>
    <td colSpan={6}>
      <EmptyQuery>
        <h6>We couldn&apos;t find any pipelines that match your search</h6>
        <p>Try changing your filters</p>
      </EmptyQuery>
    </td>
  </tr>
);

const PipelineList = ({
  isLoading,
  onPageChange,
  page,
  currentPipelines,
  pipelinesSize,
  repository,
  capabilities,
}: Props) => {
  const pages = useMemo(
    () =>
      [...Array(Math.ceil(pipelinesSize / PIPELINE_PAGE_SIZE))].map(
        (_, i) => i + 1
      ),
    [pipelinesSize]
  );

  const showLoadingSpinner = useMemo(() => {
    // Don't show loading Spinner when paginating between pages
    // which data has already been fetched for
    return isLoading && !currentPipelines.length;
  }, [currentPipelines, isLoading]);

  const showEmptyState = useMemo(() => {
    return !isLoading && !currentPipelines.length;
  }, [currentPipelines, isLoading]);

  const sendAnalyticsEvent = useCallback(
    (action: string) => {
      publishUiEvent({
        action: uiEvents.inlineStopButton.action,
        actionSubject: uiEvents.inlineStopButton.actionSubject,
        actionSubjectId: uiEvents.inlineStopButton.actionSubjectId,
        attributes: { repositoryUuid: repository.uuid, action },
        source: uiEvents.inlineStopButton.source,
      });
    },
    [repository.uuid]
  );

  return (
    <ListWrapper>
      {showLoadingSpinner ? (
        <Loading>
          <Spinner size="large" testId="pipeline-list-spinner" />
        </Loading>
      ) : (
        <div>
          <table data-testid="pipeline-list-table">
            <thead>
              <tr>
                <th id="pipeline" colSpan={2}>
                  Pipeline
                </th>
                <th id="status">Status</th>
                <th id="started">Started</th>
                <th id="duration">Duration</th>
                <th id="inline-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {showEmptyState ? (
                <EmptyState />
              ) : (
                currentPipelines.map(
                  (pipeline, i) =>
                    pipeline && (
                      <PipelineRow
                        key={`${pipeline.uuid}-${i}`}
                        pipeline={pipeline}
                        repository={repository}
                        hasPermissionToStop={canUserStopPipelines(
                          repository,
                          capabilities
                        )}
                        sendAnalyticsEvent={sendAnalyticsEvent}
                      />
                    )
                )
              )}
            </tbody>
          </table>

          {pipelinesSize > PIPELINE_PAGE_SIZE && (
            <Pagination>
              <Paginate
                pages={pages}
                selectedIndex={page - 1}
                onChange={onPageChange}
                previousLabel="Prev"
                nextLabel="Next"
              />
            </Pagination>
          )}
        </div>
      )}
    </ListWrapper>
  );
};

export default memo(PipelineList);
